import { useTranslation } from "react-i18next";
import { Check, UserCheck, X } from "lucide-react";
import { Button, Checkbox, Label } from "@/components/ui";
import { cn } from "@/lib/utils";

interface NotificationBulkBarProps {
  /** List heading shown when nothing is selected (e.g. "70 notifications"). */
  title: string;
  selectedCount: number;
  pageCount: number;
  allPageSelected: boolean;
  somePageSelected: boolean;
  canAcknowledge: boolean;
  canResolve: boolean;
  busy: boolean;
  onToggleSelectAll: () => void;
  onClearSelection: () => void;
  onAcknowledge: () => void;
  onResolve: () => void;
}

export function NotificationBulkBar({
  title,
  selectedCount,
  pageCount,
  allPageSelected,
  somePageSelected,
  canAcknowledge,
  canResolve,
  busy,
  onToggleSelectAll,
  onClearSelection,
  onAcknowledge,
  onResolve,
}: NotificationBulkBarProps) {
  const { t } = useTranslation("adminNotifications");

  if (pageCount === 0) {
    return null;
  }

  const hasSelection = selectedCount > 0;

  return (
    <div
      className={cn(
        "flex min-h-14 flex-wrap items-center justify-between gap-3 border-b border-surface-200/70 py-2.5 pl-3 pr-4 transition-colors sm:pl-4 sm:pr-5",
        hasSelection ? "bg-primary-50/60 dark:bg-primary-50/40" : "bg-surface-0",
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <Label
          spacing="none"
          title={t("bulk.hint")}
          className="flex cursor-pointer items-center rounded-lg py-1"
        >
          <span className="flex w-8 shrink-0 justify-center">
            <Checkbox
              checked={hasSelection && allPageSelected}
              indeterminate={hasSelection && somePageSelected && !allPageSelected}
              onChange={onToggleSelectAll}
              aria-label={t("bulk.selectAll")}
              className="pointer-events-none"
            />
          </span>
        </Label>
        {hasSelection ? (
          <span className="text-[13px] font-semibold text-primary-800 tabular-nums">
            {t("bulk.selected", { count: selectedCount })}
          </span>
        ) : (
          <h2 className="truncate text-[15px] font-semibold tracking-tight text-surface-900 tabular-nums">
            {title}
          </h2>
        )}
      </div>

      {hasSelection ? (
        <div className="flex flex-wrap items-center gap-2">
          {canAcknowledge && (
            <Button
              variant="outline"
              size="sm"
              isLoading={busy}
              leftIcon={<UserCheck className="h-4 w-4" />}
              onClick={onAcknowledge}
            >
              {t("bulk.acknowledge")}
            </Button>
          )}
          {canResolve && (
            <Button
              variant="primary"
              size="sm"
              isLoading={busy}
              leftIcon={<Check className="h-4 w-4" />}
              onClick={onResolve}
            >
              {t("bulk.resolve")}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<X className="h-4 w-4" />}
            onClick={onClearSelection}
            disabled={busy}
          >
            {t("bulk.clearSelection")}
          </Button>
        </div>
      ) : (
        <p className="hidden text-xs text-surface-500 tabular-nums sm:block">
          {t("bulk.pageCount", { count: pageCount })}
        </p>
      )}
    </div>
  );
}
